'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ExpertCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExpertCallModal = ({ isOpen, onClose }: ExpertCallModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    expertise: '',
    urgency: 'normal',
    message: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Expert call requested:', formData);
    onClose();
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      expertise: '',
      urgency: 'normal',
      message: ''
    });
  };

  if (!isOpen) return null;

  if (!isHydrated) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="bg-card rounded-lg p-8 max-w-2xl w-full mx-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="space-y-3">
              <div className="h-12 bg-muted rounded" />
              <div className="h-12 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-headline font-bold text-foreground">
              Request Expert Call
            </h2>
            <p className="text-sm text-muted-foreground font-body mt-1">
              Connect with our technical specialists
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-base"
          >
            <Icon name="XMarkIcon" size={24} variant="outline" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="john@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Company Name *
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Acme Corporation"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Area of Expertise *
              </label>
              <select
                name="expertise"
                value={formData.expertise}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select area...</option>
                <option value="ai-agents">AI Agents Development</option>
                <option value="data-engineering">Data Engineering</option>
                <option value="contract-staffing">Contract Staffing</option>
                <option value="copilot-workshops">Copilot Workshops</option>
                <option value="license-reselling">License Reselling</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Urgency Level
              </label>
              <select
                name="urgency"
                value={formData.urgency}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="normal">Normal (2-3 business days)</option>
                <option value="high">High (Within 24 hours)</option>
                <option value="urgent">Urgent (Same day)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-body font-semibold text-foreground mb-2">
                Additional Details
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                placeholder="Tell us about your specific needs or questions..."
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-6 bg-muted text-foreground rounded-md font-headline font-semibold hover:bg-muted/80 transition-colors duration-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-3 px-6 bg-primary text-primary-foreground rounded-md font-headline font-semibold hover:bg-secondary transition-colors duration-base"
            >
              Request Call
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpertCallModal;